import React, {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
} from 'react';

type ScrollContextProps = {
  scrollTo: (element: HTMLDivElement) => void;
};

const ScrollContext = createContext<ScrollContextProps | undefined>(undefined);

export interface ScrollProviderProps {
  children: ReactNode;
}

export const ScrollProvider = ({ children }: ScrollProviderProps) => {
  const scrollViewRef = useRef<HTMLDivElement>(null);

  const value: ScrollContextProps = useMemo(
    () => ({
      scrollTo: (element) => {
        if (element && scrollViewRef.current) {
          const { top } = element.getBoundingClientRect();
          if (top < 0 || top > window.innerHeight) {
            const headerHeight = getSiteHeaderHeight(scrollViewRef.current);
            const wixAdHeight = getWixAdHeight();
            const newTop =
              top + window.scrollY - 20 - headerHeight - wixAdHeight;
            window.scrollTo({
              top: newTop,
              behavior: 'smooth',
            });
          }
        }
      },
    }),
    [],
  );

  return (
    <div ref={scrollViewRef}>
      <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>
    </div>
  );
};

export function useScroll() {
  const context = useContext(ScrollContext);
  if (context === undefined) {
    throw new Error('useScroll must be used within a ScrollProvider');
  }
  return context;
}

export function getSiteHeaderHeight(formElement: HTMLDivElement): number {
  const classicHeader = document.querySelector('#SITE_HEADER');

  const potentialHeader = formElement.closest('main')?.previousElementSibling;
  const studioHeader =
    potentialHeader?.tagName === 'HEADER' ? potentialHeader : undefined;

  const header = classicHeader || studioHeader;
  const headerPosition = header && window.getComputedStyle(header).position;

  return header && (headerPosition === 'sticky' || headerPosition === 'fixed')
    ? header.getBoundingClientRect().height
    : 0;
}

export function getWixAdHeight(): number {
  const ad = document.querySelector('#WIX_ADS');

  return ad && window.getComputedStyle(ad).position === 'fixed'
    ? ad.getBoundingClientRect().height
    : 0;
}
